import { ChevronsUpDown, Pencil, Plus } from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useEditorStore } from "@/store/editorStore";
import { useVariablesStore } from "@/store/variablesStore";
import { toCSSVarReference } from "@/utils/cssVariables";
import { Input } from "./input";
import { Label } from "./label";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

interface NumberDropdownProps {
  value: number | null;
  onNumberChange?: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  allowSizeVariables?: boolean;
  allowAngleVariables?: boolean;
  onSelectVariable?: (value: string) => void;
  unitLabel?: string;
  className?: string;
  disabled?: boolean;
}

export const NumberDropdown = ({
  value,
  onNumberChange,
  min,
  max,
  step = 1,
  allowSizeVariables = false,
  allowAngleVariables = false,
  onSelectVariable,
  unitLabel,
  className,
  disabled,
}: NumberDropdownProps) => {
  const { projectId } = useParams<{ projectId: string }>();
  const { project } = useEditorStore();
  const {
    getProjectVariables,
    getCraftspaceVariables,
    addSizeVariable,
    addCraftspaceSizeVariable,
    updateSizeVariable,
    updateCraftspaceSizeVariable,
  } = useVariablesStore();
  const [open, setOpen] = useState(false);
  const [projectAddOpen, setProjectAddOpen] = useState(false);
  const [craftspaceAddOpen, setCraftspaceAddOpen] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [projectValue, setProjectValue] = useState("");
  const [projectUnit, setProjectUnit] = useState(allowAngleVariables ? "deg" : "px");
  const [craftspaceName, setCraftspaceName] = useState("");
  const [craftspaceValue, setCraftspaceValue] = useState("");
  const [craftspaceUnit, setCraftspaceUnit] = useState(allowAngleVariables ? "deg" : "px");
  const [projectError, setProjectError] = useState("");
  const [craftspaceError, setCraftspaceError] = useState("");
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [editingCraftspaceId, setEditingCraftspaceId] = useState<string | null>(null);
  const [editingProjectValue, setEditingProjectValue] = useState("");
  const [editingProjectUnit, setEditingProjectUnit] = useState(allowAngleVariables ? "deg" : "px");
  const [editingCraftspaceValue, setEditingCraftspaceValue] = useState("");
  const [editingCraftspaceUnit, setEditingCraftspaceUnit] = useState(allowAngleVariables ? "deg" : "px");

  const showVariables = allowSizeVariables || allowAngleVariables;

  const projectVars =
    showVariables && projectId
      ? getProjectVariables(projectId)
      : { sizes: [], angles: [], colors: [], fontFamilies: [] };
  const craftspaceVars =
    showVariables && project?.craftspace_id
      ? getCraftspaceVariables(project.craftspace_id)
      : { sizes: [], angles: [], colors: [], fontFamilies: [] };

  // Filter sizes: when allowAngleVariables only, show deg-unit sizes; otherwise show all
  const filterSizes = (sizes: typeof projectVars.sizes) => {
    if (allowAngleVariables && !allowSizeVariables) {
      return sizes.filter((s) => s.value.endsWith("deg"));
    }
    return sizes;
  };

  const projectSizes = filterSizes(projectVars.sizes);
  const craftspaceSizes = filterSizes(
    craftspaceVars.sizes.filter(
      (wsSize) => !projectVars.sizes.some((projSize) => projSize.name === wsSize.name),
    ),
  );

  // Legacy angle variables (from old store format) - shown alongside deg-unit sizes
  const legacyProjectAngles = allowAngleVariables ? (projectVars.angles || []) : [];
  const legacyCraftspaceAngles = allowAngleVariables
    ? (craftspaceVars.angles || []).filter(
        (wsAngle) => !projectVars.angles?.some((projAngle) => projAngle.name === wsAngle.name),
      )
    : [];

  const hasVariables =
    craftspaceSizes.length > 0 ||
    projectSizes.length > 0 ||
    legacyProjectAngles.length > 0 ||
    legacyCraftspaceAngles.length > 0;
  const canAdjustLocal = value !== null && onNumberChange;
  const sliderMin = min ?? (value !== null ? value - step * 10 : 0);
  const sliderMax = max ?? (value !== null ? value + step * 10 : 100);
  const sizeUnits = allowAngleVariables && !allowSizeVariables
    ? ["deg"]
    : allowAngleVariables
      ? ["px", "%", "rem", "em", "vw", "vh", "deg"]
      : ["px", "%", "rem", "em", "vw", "vh"];

  const isValidSizeName = (name: string) => /[a-zA-Z]/.test(name);

  // Parse a numeric value from a size string like "45deg" or "20px"
  const parseSizeNumber = (sizeValue: string): number | null => {
    const match = sizeValue.match(/^(-?\d+(?:\.\d+)?)/);
    return match ? Number(match[1]) : null;
  };

  // Handle clicking a size variable - for angle contexts, parse number; for size contexts, use CSS var
  const handleSelectSize = (sizeVar: { name: string; value: string }) => {
    if (allowAngleVariables && sizeVar.value.endsWith("deg")) {
      const num = parseSizeNumber(sizeVar.value);
      if (num !== null) onNumberChange?.(num);
    } else if (allowAngleVariables) {
      // Non-deg size in angle context - still parse as number
      const num = parseSizeNumber(sizeVar.value);
      if (num !== null) onNumberChange?.(num);
    } else {
      onSelectVariable?.(toCSSVarReference(sizeVar.name));
    }
    setOpen(false);
  };

  const handleAddProjectSize = () => {
    if (!projectId) return;
    const name = projectName.trim();
    if (!name || !projectValue.trim()) {
      setProjectError("Name and value are required.");
      return;
    }
    if (!isValidSizeName(name)) {
      setProjectError("Name must include at least one letter.");
      return;
    }
    const numericValue = Number(projectValue.replace(",", "."));
    if (Number.isNaN(numericValue)) {
      setProjectError("Value must be a number.");
      return;
    }
    addSizeVariable(project?.craftspace_id ?? "", projectId, { name, value: `${numericValue}${projectUnit}` });
    setProjectName("");
    setProjectValue("");
    setProjectUnit(allowAngleVariables ? "deg" : "px");
    setProjectError("");
    setProjectAddOpen(false);
  };

  const handleAddCraftspaceSize = () => {
    if (!project?.craftspace_id) return;
    const name = craftspaceName.trim();
    if (!name || !craftspaceValue.trim()) {
      setCraftspaceError("Name and value are required.");
      return;
    }
    if (!isValidSizeName(name)) {
      setCraftspaceError("Name must include at least one letter.");
      return;
    }
    const numericValue = Number(craftspaceValue.replace(",", "."));
    if (Number.isNaN(numericValue)) {
      setCraftspaceError("Value must be a number.");
      return;
    }
    addCraftspaceSizeVariable(project.craftspace_id, {
      name,
      value: `${numericValue}${craftspaceUnit}`,
    });
    setCraftspaceName("");
    setCraftspaceValue("");
    setCraftspaceUnit(allowAngleVariables ? "deg" : "px");
    setCraftspaceError("");
    setCraftspaceAddOpen(false);
  };

  const startProjectEdit = (id: string, value: string) => {
    const match = value.match(/^(-?\d+(?:\.\d+)?)(px|%|rem|em|vw|vh|deg)?$/i);
    setEditingProjectId(id);
    setEditingProjectValue(match ? match[1] : value);
    setEditingProjectUnit(match?.[2] ?? (allowAngleVariables ? "deg" : "px"));
  };

  const startCraftspaceEdit = (id: string, value: string) => {
    const match = value.match(/^(-?\d+(?:\.\d+)?)(px|%|rem|em|vw|vh|deg)?$/i);
    setEditingCraftspaceId(id);
    setEditingCraftspaceValue(match ? match[1] : value);
    setEditingCraftspaceUnit(match?.[2] ?? (allowAngleVariables ? "deg" : "px"));
  };

  const applyProjectEdit = () => {
    if (!editingProjectId || !projectId) return;
    const numericValue = Number(editingProjectValue.replace(",", "."));
    if (Number.isNaN(numericValue)) {
      setProjectError("Value must be a number.");
      return;
    }
    updateSizeVariable(project?.craftspace_id ?? "", projectId, editingProjectId, {
      value: `${numericValue}${editingProjectUnit}`,
    });
    setEditingProjectId(null);
    setEditingProjectValue("");
    setEditingProjectUnit(allowAngleVariables ? "deg" : "px");
    setProjectError("");
  };

  const applyCraftspaceEdit = () => {
    if (!editingCraftspaceId || !project?.craftspace_id) return;
    const numericValue = Number(editingCraftspaceValue.replace(",", "."));
    if (Number.isNaN(numericValue)) {
      setCraftspaceError("Value must be a number.");
      return;
    }
    updateCraftspaceSizeVariable(project.craftspace_id, editingCraftspaceId, {
      value: `${numericValue}${editingCraftspaceUnit}`,
    });
    setEditingCraftspaceId(null);
    setEditingCraftspaceValue("");
    setEditingCraftspaceUnit(allowAngleVariables ? "deg" : "px");
    setCraftspaceError("");
  };

  const handleProjectKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleAddProjectSize();
    }
  };
  const handleCraftspaceKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleAddCraftspaceSize();
    }
  };

  const sectionLabel = allowAngleVariables && !allowSizeVariables ? "Angles" : "Sizes";

  const renderSizeRow = (sizeVar: { id: string; name: string; value: string }, scope: "project" | "craftspace") => {
    const isEditing = scope === "project" ? editingProjectId === sizeVar.id : editingCraftspaceId === sizeVar.id;
    return (
      <div
        key={scope === "craftspace" ? `ws-${sizeVar.id}` : sizeVar.id}
        className="w-full rounded bg-gray-700 hover:bg-gray-600 text-left px-2 py-1"
      >
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => handleSelectSize(sizeVar)}
            className="flex items-center justify-between w-full text-left"
            disabled={isEditing}
          >
            <span className="text-xs font-medium text-white truncate">
              {sizeVar.name}
            </span>
            <span className="text-xs text-gray-400 ml-2 flex-shrink-0">
              {sizeVar.value}
            </span>
          </button>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-200"
            onClick={() => scope === "project" ? startProjectEdit(sizeVar.id, sizeVar.value) : startCraftspaceEdit(sizeVar.id, sizeVar.value)}
            aria-label={`Edit ${sizeVar.name}`}
          >
            <Pencil size={12} />
          </button>
        </div>
        {isEditing && (
          <div className="mt-2 flex items-center gap-2">
            <Input
              value={scope === "project" ? editingProjectValue : editingCraftspaceValue}
              onChange={(e) => scope === "project" ? setEditingProjectValue(e.target.value) : setEditingCraftspaceValue(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  scope === "project" ? applyProjectEdit() : applyCraftspaceEdit();
                } else if (event.key === "Escape") {
                  scope === "project" ? setEditingProjectId(null) : setEditingCraftspaceId(null);
                }
              }}
              className="h-7 bg-gray-700 border-gray-600 text-white text-xs"
            />
            <select
              value={scope === "project" ? editingProjectUnit : editingCraftspaceUnit}
              onChange={(e) => scope === "project" ? setEditingProjectUnit(e.target.value) : setEditingCraftspaceUnit(e.target.value)}
              className="h-7 bg-gray-700 border border-gray-600 text-white text-xs rounded px-2"
            >
              {sizeUnits.map((unit) => (
                <option key={unit} value={unit}>
                  {unit}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    );
  };

  // Render a legacy angle variable (backward compat for angles stored in old format)
  const renderLegacyAngleRow = (angle: { id: string; name: string; value: number }) => (
    <div
      key={`legacy-${angle.id}`}
      className="w-full rounded bg-gray-700 hover:bg-gray-600 text-left px-2 py-1"
    >
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => {
            onNumberChange?.(angle.value);
            setOpen(false);
          }}
          className="flex items-center justify-between w-full text-left"
        >
          <span className="text-xs font-medium text-white truncate">
            {angle.name}
          </span>
          <span className="text-xs text-gray-400 ml-2 flex-shrink-0">
            {angle.value}°
          </span>
        </button>
      </div>
    </div>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "flex items-center gap-1 text-gray-400 hover:text-gray-200 disabled:opacity-60",
            className,
          )}
          aria-label="Toggle number options"
        >
          <ChevronsUpDown size={14} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3 bg-gray-800 border-gray-700" align="end" side="bottom">
        <div className="space-y-3">
          {showVariables && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-gray-300 text-sm mb-1 block">Project {sectionLabel}</Label>
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-200"
                  onClick={() => {
                    setProjectAddOpen((prev) => !prev);
                    setProjectError("");
                  }}
                  aria-label={`Add project ${sectionLabel.toLowerCase()} variable`}
                >
                  <Plus size={12} />
                </button>
              </div>
              {projectAddOpen && (
                <div className="space-y-2 rounded bg-gray-700/50 border border-gray-600 p-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-300">Value</Label>
                    <div className="flex gap-2">
                      <Input
                        value={projectValue}
                        onChange={(e) => {
                          setProjectValue(e.target.value);
                          if (projectError) setProjectError("");
                        }}
                        onKeyDown={handleProjectKeyDown}
                        placeholder={allowAngleVariables ? "45" : "5"}
                        className="h-8 bg-gray-700 border-gray-600 text-white text-xs"
                      />
                      {sizeUnits.length === 1 ? (
                        <div className="h-8 px-2 rounded border border-gray-600 text-xs text-gray-300 flex items-center bg-gray-700">
                          {sizeUnits[0] === "deg" ? "°" : sizeUnits[0]}
                        </div>
                      ) : (
                        <select
                          value={projectUnit}
                          onChange={(e) => setProjectUnit(e.target.value)}
                          className="h-8 bg-gray-700 border border-gray-600 text-white text-xs rounded px-2"
                        >
                          {sizeUnits.map((unit) => (
                            <option key={unit} value={unit}>
                              {unit}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-300">Name</Label>
                    <Input
                      value={projectName}
                      onChange={(e) => {
                        setProjectName(e.target.value);
                        if (projectError) setProjectError("");
                      }}
                      onKeyDown={handleProjectKeyDown}
                      placeholder={allowAngleVariables ? "e.g., north" : "e.g., small"}
                      className="h-8 bg-gray-700 border-gray-600 text-white text-xs"
                    />
                  </div>
                  {projectError && <div className="text-[11px] text-red-400">{projectError}</div>}
                </div>
              )}
              {projectSizes.length === 0 && legacyProjectAngles.length === 0 ? (
                <div className="text-xs text-gray-400">No project {sectionLabel.toLowerCase()} yet.</div>
              ) : (
                <div className="space-y-1">
                  {projectSizes.map((sizeVar) => renderSizeRow(sizeVar, "project"))}
                  {legacyProjectAngles.map((angle) => renderLegacyAngleRow(angle))}
                </div>
              )}

              <div className="flex items-center justify-between">
                <Label className="text-gray-300 text-sm mb-1 block">Craftspace {sectionLabel}</Label>
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-200"
                  onClick={() => {
                    setCraftspaceAddOpen((prev) => !prev);
                    setCraftspaceError("");
                  }}
                  aria-label={`Add craftspace ${sectionLabel.toLowerCase()} variable`}
                >
                  <Plus size={12} />
                </button>
              </div>
              {craftspaceAddOpen && (
                <div className="space-y-2 rounded bg-gray-700/50 border border-gray-600 p-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-300">Value</Label>
                    <div className="flex gap-2">
                      <Input
                        value={craftspaceValue}
                        onChange={(e) => {
                          setCraftspaceValue(e.target.value);
                          if (craftspaceError) setCraftspaceError("");
                        }}
                        onKeyDown={handleCraftspaceKeyDown}
                        placeholder={allowAngleVariables ? "45" : "5"}
                        className="h-8 bg-gray-700 border-gray-600 text-white text-xs"
                      />
                      {sizeUnits.length === 1 ? (
                        <div className="h-8 px-2 rounded border border-gray-600 text-xs text-gray-300 flex items-center bg-gray-700">
                          {sizeUnits[0] === "deg" ? "°" : sizeUnits[0]}
                        </div>
                      ) : (
                        <select
                          value={craftspaceUnit}
                          onChange={(e) => setCraftspaceUnit(e.target.value)}
                          className="h-8 bg-gray-700 border border-gray-600 text-white text-xs rounded px-2"
                        >
                          {sizeUnits.map((unit) => (
                            <option key={unit} value={unit}>
                              {unit}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-300">Name</Label>
                    <Input
                      value={craftspaceName}
                      onChange={(e) => {
                        setCraftspaceName(e.target.value);
                        if (craftspaceError) setCraftspaceError("");
                      }}
                      onKeyDown={handleCraftspaceKeyDown}
                      placeholder={allowAngleVariables ? "e.g., north" : "e.g., small"}
                      className="h-8 bg-gray-700 border-gray-600 text-white text-xs"
                    />
                  </div>
                  {craftspaceError && (
                    <div className="text-[11px] text-red-400">{craftspaceError}</div>
                  )}
                </div>
              )}
              {craftspaceSizes.length === 0 && legacyCraftspaceAngles.length === 0 ? (
                <div className="text-xs text-gray-400">No craftspace {sectionLabel.toLowerCase()} yet.</div>
              ) : (
                <div className="space-y-1">
                  {craftspaceSizes.map((sizeVar) => renderSizeRow(sizeVar, "craftspace"))}
                  {legacyCraftspaceAngles.map((angle) => renderLegacyAngleRow(angle))}
                </div>
              )}
            </div>
          )}

          <div>
            <Label className="text-gray-300 text-sm mb-1 block">Local Value</Label>
            {!canAdjustLocal ? (
              <div className="text-xs text-gray-400">
                Enter a numeric value to enable local adjustments.
              </div>
            ) : (
              <div className="space-y-2">
                <Input
                  type="range"
                  min={sliderMin}
                  max={sliderMax}
                  step={step}
                  value={value}
                  onChange={(e) => onNumberChange?.(parseFloat(e.target.value))}
                  className="colorpicker-slider cursor-pointer"
                />
                <div className="text-xs text-gray-400 text-center">
                  {value}
                  {unitLabel ? unitLabel : ""}
                </div>
              </div>
            )}
          </div>

          {!showVariables && !canAdjustLocal && !hasVariables && (
            <div className="text-xs text-gray-500">No options available.</div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
